import React, { useMemo } from 'react';
import { Box, SxProps, Tooltip } from '@mui/material';
import { NodonTheme } from '@client/style';
import { useProjectTarget } from '@client/store/project';
import { getWidthInPercent } from '@ui/BarChart/helpers/bar-chart.helpers';
import {
  AcceptedDisplayFormatUnits,
  getDisplayValueData,
  SupportedDivideByUnit,
} from '@shared/helpers/display/get-display-value-data.helper';
import { Project } from '@shared/models/project.model';
import { guards } from '@shared/helpers/typeguards.helpers';

/**
 * Get the label for the target indicator.
 */
export const getTargetLabel = <Fallback,>(
  value: Project['target'],
  unit: AcceptedDisplayFormatUnits,
  divideBy: SupportedDivideByUnit,
  fallback: Fallback,
): string | Fallback => {
  if (!guards.is.number(value)) return fallback;
  return `Target: ${getDisplayValueData({ value, unit }, { divideBy: { unit: divideBy } }).full}`;
};

interface TargetIndicatorProps {
  maxValue: number;
  projectId?: number | string;
  disableTooltip?: boolean;
}

/**
 * Project-list bar. Reads the leftover {@link Project.target} column, not
 * {@link ProjectMetadata.scope_targets}.
 */
const TargetIndicator: React.FC<TargetIndicatorProps> = ({
  maxValue,
  projectId,
  disableTooltip = false,
}) => {
  const target = useProjectTarget(projectId);
  const targetValue = Math.min(target ?? 0, maxValue); // if no target, use 0 + never surpass maxValue
  const offset = getWidthInPercent(targetValue, maxValue);

  const targetStyles = useMemo<SxProps>(
    () => ({
      ...hoverAreaStyles,
      left: `calc(${offset}% - ${HOVER_AREA_WIDTH / 2}px)`,
    }),
    [offset],
  );
  const tooltip = disableTooltip
    ? undefined
    : getTargetLabel(target, 'kgCO2e', 'm^2', undefined);
  return (
    !!target && (
      <Tooltip disableInteractive title={tooltip}>
        <Box sx={targetStyles}>
          <Box sx={indicatorStyles} />
        </Box>
      </Tooltip>
    )
  );
};

const HOVER_AREA_WIDTH = 8;

const indicatorStyles: SxProps = {
  height: '100%',
  width: '1px',
  backgroundImage: `repeating-linear-gradient(to bottom, ${NodonTheme.palette.neutral.light} 0 1px, transparent 1px 2px)`,
};

const hoverAreaStyles: SxProps = {
  transition: 'left 0.5s ease',
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
  height: '100%',
  zIndex: 0,
  top: 0,
  width: HOVER_AREA_WIDTH,
};

export default TargetIndicator;
